import { useEffect, useRef, useState } from 'react'

import { showToast } from '../Toast'
import { Button } from '../Button'
import { flagDeck } from '../../lib/deck-mod-api.js'
import styles from './FlagDeckButton.module.css'

const MAX_REASON_LENGTH = 500

interface FlagDeckButtonProps {
  deckId: string
  alreadyFlagged: boolean
  getIdToken: () => Promise<string>
}

export function FlagDeckButton({
  deckId,
  alreadyFlagged,
  getIdToken,
}: FlagDeckButtonProps): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [flagged, setFlagged] = useState(alreadyFlagged)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) {
      dialog.showModal()
    } else if (!open && dialog.open) {
      dialog.close()
    }
  }, [open])

  function handleOpen() {
    setReason('')
    setSubmitError(null)
    setOpen(true)
  }

  function handleClose() {
    setOpen(false)
    triggerRef.current?.focus()
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setSubmitError(null)
    try {
      await flagDeck(getIdToken, deckId, reason.trim() ? { reason: reason.trim() } : {})
      setFlagged(true)
      setOpen(false)
      showToast('Thanks — a moderator will review this deck.', 'success')
    } catch {
      setSubmitError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size="sm"
        disabled={flagged}
        onClick={handleOpen}
        aria-label={flagged ? 'You have flagged this deck' : 'Flag this deck for review'}
      >
        {flagged ? 'Flagged' : 'Flag this deck'}
      </Button>

      <dialog
        ref={dialogRef}
        className={styles.dialog}
        aria-labelledby="flag-dialog-title"
        onCancel={handleClose}
        onClose={handleClose}
      >
        <div className={styles.panel}>
          <header className={styles.header}>
            <h2 id="flag-dialog-title" className={styles.title}>
              Report a problem
            </h2>
          </header>

          <form onSubmit={(e) => void handleSubmit(e)}>
            <div className={styles.field}>
              <label htmlFor="flag-reason" className={styles.label}>
                Reason <span className={styles.optional}>(optional)</span>
              </label>
              <textarea
                id="flag-reason"
                className={styles.textarea}
                value={reason}
                maxLength={MAX_REASON_LENGTH}
                rows={4}
                placeholder="Describe the issue…"
                onChange={(e) => setReason(e.currentTarget.value)}
              />
              <p className={styles.charCount} aria-live="polite">
                {reason.length}/{MAX_REASON_LENGTH}
              </p>
            </div>

            {submitError ? (
              <p className={styles.error} role="alert">
                {submitError}
              </p>
            ) : null}

            <div className={styles.footer}>
              <Button type="button" variant="secondary" disabled={submitting} onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={submitting}>
                Submit report
              </Button>
            </div>
          </form>
        </div>
      </dialog>
    </>
  )
}
